import { z } from 'zod';

// ---------------------------------------------------------------------------
// Live Page Context (#274). A snapshot of what the consultant is currently
// looking at, sent on every agent-loop call. The loop renders it into the
// system prompt so deictic phrases ("this", "these", "sort this") resolve,
// and the (trimmed) snapshot is persisted on the audit row for replay.
//
// SECURITY: `permissions` here is CLIENT-ASSERTED and descriptive only — it
// is never the authorisation boundary. Tool RBAC stays enforced server-side
// against `ctx.user.permissions`. Do not gate writes on this field.
//
// `.strict()` drops unknown fields at the wire boundary; size caps mirror the
// PRD (`selectedRowIds` ≤ 200). The `currentFilters` 4 KB byte cap is enforced
// server-side (see `sanitizeSnapshot`), not in Zod, since byte length isn't a
// schema concern.
// ---------------------------------------------------------------------------
export const pageSnapshotSchema = z
  .object({
    // The current route pathname (e.g. `/itineraries`, `/itineraries/:id`).
    route: z.string().max(512),
    // The page's applied URL search params, serialised as a plain object.
    // Shape is the page's own `validateXxxSearch` output — kept opaque here.
    currentFilters: z.record(z.string(), z.unknown()).optional(),
    // Column ids currently visible in the page's table, when it has one.
    visibleColumns: z.array(z.string().max(128)).max(64).optional(),
    // Ids of the rows the consultant has selected — resolves "these".
    selectedRowIds: z.array(z.string().max(128)).max(200).optional(),
    // The caller's permission strings (`resource:action`). Descriptive only.
    permissions: z.array(z.string().max(128)).max(256).optional(),
    // Per-page payload. The canonical use is `{ itineraryId, dayId }` on the
    // itinerary detail page so write tools can resolve "this itinerary".
    extras: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export type PageSnapshot = z.infer<typeof pageSnapshotSchema>;

// ---------------------------------------------------------------------------
// Conversation memory (P2.5). A bounded, client-passed transcript of recent
// agent-loop turns, replayed on each call so the agent remembers context
// ("add another like that", "undo the last thing"). Mirrors the `pageSnapshot`
// pattern: client owns the ephemeral per-page-session state and ferries it
// back each turn; the server caps + sanitises it and renders it into the
// system prompt. Like `permissions`, it is DESCRIPTIVE ONLY — never an
// authorisation input.
//
// Kept small on purpose: only what the model needs to resolve back-references,
// plus the `undoToken`/`runId` so a prior turn's writes can be targeted for
// cross-turn undo (P2.6). Total turns capped at 6; the client drops the oldest.
// ---------------------------------------------------------------------------
export const agentTranscriptTurnSchema = z.object({
  // The consultant's request for this turn.
  goal: z.string().max(1_000),
  // The one-line summary the agent reported when the turn finished.
  finalText: z.string().max(2_000),
  // Compact record of what the turn did — tool labels + key result summaries,
  // never full payloads. Drives "what did you just change".
  actions: z
    .array(
      z.object({
        label: z.string().max(256),
        summary: z.string().max(512),
      })
    )
    .max(20)
    .optional(),
  // The turn's undo token (if it made reversible writes), so "undo the last
  // thing you did" can target it across turns. Null for read-only turns.
  undoToken: z.string().nullable().optional(),
  // The run id the server stamped on this turn's final event.
  runId: z.string().max(128).optional(),
  // ISO timestamp of the turn (UTC), informational.
  at: z.string().max(40).optional(),
});

export const agentTranscriptSchema = z.array(agentTranscriptTurnSchema).max(6);

export type AgentTranscriptTurn = z.infer<typeof agentTranscriptTurnSchema>;
export type AgentTranscript = z.infer<typeof agentTranscriptSchema>;
